/**
 * Yallet RWA SDK - Type Definitions
 */

// ======================================
//  Asset Types
// ======================================

/**
 * Supported RWA asset types
 * Matches chrome-extension RWA_ASSET_TYPES for compatibility (no statement/receipt in extension).
 */
export enum AssetType {
  NOTE = 'note',
  INVOICE = 'invoice',
  PHOTO = 'photo',
  FILE = 'file',
  CONTACT = 'contact',
}

/**
 * Invoice data structure
 * Aligns with chrome-extension invoice format for decrypt compatibility.
 */
export interface InvoiceData {
  /** Unique invoice ID/number (extension: invoiceNo) */
  invoiceId: string;
  /** Invoice amount / total */
  amount: number;
  /** Currency code (e.g., 'USD', 'EUR') */
  currency: string;
  /** Invoice date (ISO string) */
  date: string;
  /** Due date (ISO string) (extension: dueDate) */
  dueDate?: string;
  /** Invoice description */
  description?: string;
  /** Line items (extension: lineItems with name, price, qty, unit, total) */
  items?: InvoiceItem[];
  /** Additional notes (extension: notePayee) */
  notes?: string;
  /** Custom metadata */
  metadata?: Record<string, unknown>;
  /** Extension-compat: billTo contact id */
  billTo?: string;
  /** Extension-compat: PO number */
  poNo?: string;
  /** Extension-compat: discount rate 0-100 */
  discountRate?: number;
  /** Extension-compat: discount amount */
  discountAmount?: number;
  /** Extension-compat: tax rate 0-100 */
  taxRate?: number;
  /** Extension-compat: subtotal */
  subtotal?: number;
  /** Extension-compat: tax amount */
  taxAmount?: number;
  /** Extension-compat: total (same as amount) */
  total?: number;
  /** Extension-compat: payment method 'bank' | 'crypto' */
  paymentMethod?: string;
  /** Extension-compat: payment terms */
  paymentTerms?: string;
  /** Extension-compat: note to payee */
  notePayee?: string;
}

export interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  /** Extension-compat: line item name (same as description) */
  name?: string;
  /** Extension-compat: unit e.g. 'units' | 'hours' */
  unit?: string;
  /** Extension-compat: qty (same as quantity) */
  qty?: number;
  /** Extension-compat: total (same as amount) */
  total?: number;
}

/**
 * Note data structure
 */
export interface NoteData {
  title: string;
  content: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

/**
 * Contact data structure
 */
export interface ContactData {
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  address?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
}

/**
 * File data structure
 */
export interface FileData {
  filename: string;
  mimeType: string;
  /** Base64 encoded file content */
  content: string;
  size: number;
  metadata?: Record<string, unknown>;
}

/**
 * Photo data structure
 */
export interface PhotoData {
  filename: string;
  mimeType: string;
  /** Base64 encoded image content */
  content: string;
  width?: number;
  height?: number;
  metadata?: Record<string, unknown>;
}

// ======================================
//  Encryption Types
// ======================================

/**
 * Encrypted payload structure (ECIES)
 */
export interface EncryptedPayload {
  /** Ephemeral public key (base64) */
  ephemeral_pub: string;
  /** Encrypted AES key (base64) */
  encrypted_aes_key: string;
  /** Initialization vector (base64) */
  iv: string;
  /** Encrypted data (base64) */
  encrypted_data: string;
}

/**
 * Asset envelope for encryption
 */
export interface AssetEnvelope {
  version: number;
  type: AssetType;
  timestamp: number;
  uuid: string;
  schema?: string;
  previousToken?: string;
  data: unknown;
}

/**
 * Encrypt function signature (compatible with WASM)
 */
export type EncryptFunction = (
  recipientXidentityB64: string,
  plaintext: Uint8Array
) => string; // Returns JSON string or "error:..."

// ======================================
//  User Registry Types
// ======================================

/**
 * Registered user with xidentity
 */
export interface RegisteredUser {
  /** User's Solana wallet address */
  solanaAddress: string;
  /** User's xidentity public key (base64) */
  xidentity: string;
  /** Optional display name */
  name?: string;
  /** Optional email */
  email?: string;
  /** Registration timestamp */
  registeredAt: number;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * User registry interface
 */
export interface UserRegistry {
  /** Get user by Solana address */
  getUser(solanaAddress: string): Promise<RegisteredUser | null>;
  /** Register a new user */
  registerUser(user: RegisteredUser): Promise<void>;
  /** Update user */
  updateUser(solanaAddress: string, updates: Partial<RegisteredUser>): Promise<void>;
  /** Delete user */
  deleteUser(solanaAddress: string): Promise<void>;
  /** List all users */
  listUsers(): Promise<RegisteredUser[]>;
}

// ======================================
//  Minting Types
// ======================================

/**
 * Mint request parameters
 */
export interface MintRequest {
  /** Asset type */
  assetType: AssetType;
  /** Asset data (InvoiceData, NoteData, ContactData, FileData, PhotoData) */
  assetData: InvoiceData | NoteData | ContactData | FileData | PhotoData;
  /** Recipient's Solana address */
  recipientAddress: string;
  /** NFT name */
  name: string;
  /** NFT description */
  description?: string;
  /** Additional NFT attributes */
  attributes?: Array<{ trait_type: string; value: string }>;
  /** Unique identifier for tracking (auto-generated if not provided) */
  uuid?: string;
  /** Previous token address for version chain (null for first version) */
  previousToken?: string;
  /** Schema identifier for data format validation */
  schema?: string;
  /** MIME type of the asset content */
  mimeType?: string;
  /** Size of the encrypted data in bytes */
  dataSize?: number;
}

/**
 * Mint result
 */
export interface MintResult {
  /** Whether minting was successful */
  success: boolean;
  /** Asset ID (Solana address of the NFT) */
  assetId?: string;
  /** Transaction signature */
  signature?: string;
  /** Arweave transaction ID for encrypted data */
  arweaveTxId?: string;
  /** Arweave transaction ID for metadata */
  metadataArweaveTxId?: string;
  /** UUID used for this mint */
  uuid?: string;
  /** Error message if failed */
  error?: string;
}

// ======================================
//  Tree Management Types
// ======================================

/**
 * Merkle tree configuration
 */
export interface TreeConfig {
  /** Tree address (Solana pubkey) */
  treeAddress: string;
  /** Tree authority address */
  treeAuthority: string;
  /** Maximum depth */
  maxDepth: number;
  /** Total capacity */
  capacity: number;
  /** Current usage */
  used: number;
  /** Remaining slots */
  remaining: number;
  /** Usage percentage */
  usagePercent: number;
}

// ======================================
//  SDK Configuration Types
// ======================================

/**
 * SDK configuration options
 */
export interface SDKConfig {
  /** Solana RPC endpoint */
  rpcEndpoint: string;
  /** Network: 'mainnet' or 'devnet' */
  network: 'mainnet' | 'devnet';
  /** Merkle tree address (optional, will use backend if not provided) */
  merkleTreeAddress?: string;
  /** Merkle tree authority */
  merkleTreeAuthority?: string;
  /** Arweave upload endpoint */
  arweaveEndpoint?: string;
  /** Arweave API key for uploads (ArDrive Turbo) */
  arweaveApiKey?: string;
  /** Backend API URL for tree management */
  backendApiUrl?: string;
}

/**
 * Signer interface for transaction signing
 */
export interface Signer {
  /** Solana public key (base58) */
  publicKey: string;
  /** Sign transaction */
  signTransaction(tx: Uint8Array): Promise<Uint8Array>;
  /** Sign message */
  signMessage(message: Uint8Array): Promise<Uint8Array>;
}
